/**
 * Focus a Repancy desktop window (bring to front).
 *
 * Wraps WindowContext.focusWindow, which sets the highest zIndex on the
 * target path. If the window is minimized, the SPA's focus handler
 * restores it.
 */

import type { NipeTool } from './types';
import { NIPE_KNOWN_WINDOWS } from './openWindow';

export interface FocusWindowContext {
  focusWindow?: (path: string) => Promise<{ focused: boolean }>;
}

const KNOWN_PATHS = NIPE_KNOWN_WINDOWS.map((w) => w.path);

export const focusWindowTool: NipeTool<FocusWindowContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'focusWindow',
      description:
        'Bring a Repancy desktop window to the front. Use when the user ' +
        'asks to switch to, look at, or focus a window that may be open ' +
        'but obscured. Known paths: ' + KNOWN_PATHS.join(', '),
      parameters: {
        type: 'object',
        properties: {
          path: {
            type: 'string',
            description: 'Window path. One of: ' + KNOWN_PATHS.join(', '),
          },
        },
        required: ['path'],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: false,
  handler: async (args, ctx) => {
    if (!ctx.focusWindow) {
      return { error: 'focusWindow integration is not available in this session.' };
    }
    const path = typeof args.path === 'string' ? args.path : '';
    if (!KNOWN_PATHS.includes(path)) {
      return {
        error: `Unknown window path "${path}". Known: ${KNOWN_PATHS.join(', ')}`,
      };
    }
    return await ctx.focusWindow(path);
  },
};
